import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, CreditCard, ListTree, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface Deposit {
    id: string;
    deposit_type: "percent" | "fixed";
    percent: string | null;
    fixed_amount: string | null;
    amount_due: string;
    amount_collected: string;
    status: "pending" | "collected" | "refunded" | "waived";
    invoice_id: string | null;
}
interface Milestone {
    id: string;
    sequence: number;
    label: string;
    amount_type: "fixed" | "percent";
    percent: string | null;
    amount: string;
    due_condition: string | null;
    due_date: string | null;
    status: "scheduled" | "ready" | "invoiced" | "paid" | "cancelled";
    invoice_id: string | null;
}

function money(v: string | number | null | undefined): string {
    const n = v === null || v === undefined ? 0 : Number(v) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function EstimateDepositPanel({ estimateId }: { estimateId: string }) {
    const qc = useQueryClient();
    const depositQ = useQuery<Deposit | null>({
        queryKey: ["estimate-deposit", estimateId],
        queryFn: () => api.get<Deposit | null>(`/api/estimates/${estimateId}/deposit`),
    });
    const milestonesQ = useQuery<{ items: Milestone[] }>({
        queryKey: ["estimate-milestones", estimateId],
        queryFn: () => api.get<{ items: Milestone[] }>(`/api/estimates/${estimateId}/milestones`),
    });

    const [depType, setDepType] = useState<"percent" | "fixed">("percent");
    const [depValue, setDepValue] = useState("25");

    const saveDeposit = useMutation({
        mutationFn: () =>
            api.put(`/api/estimates/${estimateId}/deposit`, {
                deposit_type: depType,
                percent: depType === "percent" ? Number(depValue) : undefined,
                fixed_amount: depType === "fixed" ? Number(depValue) : undefined,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate-deposit", estimateId] });
            toast.success("Deposit saved");
        },
        onError: (e: any) => toast.error(e.message ?? "Failed"),
    });

    const clearDeposit = useMutation({
        mutationFn: () => api.delete(`/api/estimates/${estimateId}/deposit`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate-deposit", estimateId] });
            toast.success("Deposit cleared");
        },
    });

    const markCollected = useMutation({
        mutationFn: (amount: number) => api.post(`/api/estimates/${estimateId}/deposit/mark-collected`, { amount }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate-deposit", estimateId] });
            toast.success("Deposit recorded");
        },
    });

    const charge = useMutation({
        mutationFn: () =>
            api.post<{ clientSecret?: string; deposit_invoice_id?: string }>(
                `/api/estimates/${estimateId}/deposit/charge`,
                {},
            ),
        onSuccess: (r: any) => {
            toast.success("Stripe deposit intent created");
            if (r?.deposit_invoice_id) {
                qc.invalidateQueries({ queryKey: ["estimate-deposit", estimateId] });
            }
        },
        onError: (e: any) => toast.error(e.message ?? "Failed"),
    });

    const dep = depositQ.data;

    /* Milestone form */
    const [msLabel, setMsLabel] = useState("");
    const [msType, setMsType] = useState<"fixed" | "percent">("fixed");
    const [msValue, setMsValue] = useState("");

    const addMilestone = useMutation({
        mutationFn: () =>
            api.post(`/api/estimates/${estimateId}/milestones`, {
                label: msLabel,
                amount_type: msType,
                amount: msType === "fixed" ? Number(msValue || 0) : 0,
                percent: msType === "percent" ? Number(msValue || 0) : null,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate-milestones", estimateId] });
            setMsLabel("");
            setMsValue("");
            toast.success("Milestone added");
        },
    });

    const deleteMilestone = useMutation({
        mutationFn: (id: string) => api.delete(`/api/milestones/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate-milestones", estimateId] }),
    });

    const invoiceMilestone = useMutation({
        mutationFn: (id: string) => api.post(`/api/milestones/${id}/invoice`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate-milestones", estimateId] });
            toast.success("Milestone invoiced");
        },
        onError: (e: any) => toast.error(e.message ?? "Failed"),
    });

    return (
        <div className="grid gap-4 md:grid-cols-2">
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-sky-500" /> Deposit
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    {dep ? (
                        <>
                            <div className="flex items-center justify-between">
                                <span className="text-foreground/80">Due</span>
                                <span className="font-semibold">{money(dep.amount_due)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-foreground/80">Collected</span>
                                <span>{money(dep.amount_collected)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-foreground/80">Status</span>
                                <Badge variant="outline" className="capitalize">
                                    {dep.status}
                                </Badge>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button size="sm" onClick={() => charge.mutate()} disabled={dep.status === "collected"}>
                                    Charge via Stripe
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => markCollected.mutate(Number(dep.amount_due))}
                                    disabled={dep.status === "collected"}
                                >
                                    <CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600"
                                    onClick={() => clearDeposit.mutate()}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-foreground/80">No deposit configured.</p>
                    )}

                    <div className="pt-3 border-t space-y-2">
                        <label className="text-xs text-foreground/80">{dep ? "Update deposit" : "Add deposit"}</label>
                        <div className="flex gap-2">
                            <select
                                className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                value={depType}
                                onChange={(e) => setDepType(e.target.value as any)}
                            >
                                <option value="percent">%</option>
                                <option value="fixed">$</option>
                            </select>
                            <Input
                                type="number"
                                value={depValue}
                                onChange={(e) => setDepValue(e.target.value)}
                                className="flex-1"
                            />
                            <Button size="sm" onClick={() => saveDeposit.mutate()}>
                                Save
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                        <ListTree className="h-4 w-4 text-emerald-500" /> Milestone Schedule
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    {milestonesQ.data?.items?.length ? (
                        <ul className="divide-y divide-border">
                            {milestonesQ.data.items.map((m) => (
                                <li key={m.id} className="py-2 flex items-center gap-2">
                                    <span className="text-xs text-foreground/80 w-5">{m.sequence + 1}.</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="truncate font-medium">{m.label}</p>
                                        {m.due_condition && (
                                            <p className="text-xs text-foreground/80 truncate">{m.due_condition}</p>
                                        )}
                                    </div>
                                    <span className="whitespace-nowrap">{money(m.amount)}</span>
                                    <Badge variant="outline" className="capitalize text-xs">
                                        {m.status}
                                    </Badge>
                                    {m.status === "scheduled" || m.status === "ready" ? (
                                        <Button size="sm" variant="ghost" onClick={() => invoiceMilestone.mutate(m.id)}>
                                            Invoice
                                        </Button>
                                    ) : null}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600"
                                        onClick={() => deleteMilestone.mutate(m.id)}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-foreground/80">No milestones defined.</p>
                    )}

                    <div className="pt-3 border-t space-y-2">
                        <label className="text-xs text-foreground/80">Add milestone</label>
                        <Input
                            placeholder="e.g. 50% on rough-in complete"
                            value={msLabel}
                            onChange={(e) => setMsLabel(e.target.value)}
                        />
                        <div className="flex gap-2">
                            <select
                                className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                value={msType}
                                onChange={(e) => setMsType(e.target.value as any)}
                            >
                                <option value="fixed">$</option>
                                <option value="percent">%</option>
                            </select>
                            <Input
                                type="number"
                                value={msValue}
                                onChange={(e) => setMsValue(e.target.value)}
                                className="flex-1"
                            />
                            <Button
                                size="sm"
                                onClick={() => addMilestone.mutate()}
                                disabled={!msLabel.trim() || !msValue}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
